import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/ui/page-header';
import { PDFViewer } from '@/components/PDFViewer';
import { WordOnlineViewer } from '@/components/WordOnlineViewer';
import { useAuth } from '@/hooks/useAuth';
import { ResumeService, Resume } from '@/services/resumeService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Eye, EyeOff, FileText, Download, ArrowLeft, Image, Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { createWorker } from 'tesseract.js';

export default function DocumentScrapingTest() {
  const { toast } = useToast();
  const { user } = useAuth();
  const hiddenViewerRef = useRef<HTMLDivElement>(null);
  
  const [userResumes, setUserResumes] = useState<Resume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [selectedResume, setSelectedResume] = useState<Resume | null>(null);
  const [isLoadingResumes, setIsLoadingResumes] = useState(false);
  const [isViewerVisible, setIsViewerVisible] = useState(true);
  const [isScraping, setIsScraping] = useState(false);
  const [scrapedText, setScrapedText] = useState('');
  const [scrapingProgress, setScrapingProgress] = useState(0);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);

  // Image OCR states
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isOCRProcessing, setIsOCRProcessing] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrText, setOcrText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load user's resumes
  const loadResumes = async () => {
    if (!user?.id) {
      toast({
        title: "Authentication required",
        description: "Please log in to load your resumes.",
        variant: "destructive"
      });
      return;
    }

    setIsLoadingResumes(true);
    try {
      const { resumes, error } = await ResumeService.getUserResumes(user.id);
      if (error) {
        throw new Error(error.message || 'Failed to load resumes');
      }
      setUserResumes(resumes || []);
      toast({
        title: "Resumes loaded",
        description: `Found ${resumes?.length || 0} resumes in your library.`
      });
    } catch (error) {
      console.error('Error loading resumes:', error);
      toast({
        title: "Error loading resumes",
        description: error instanceof Error ? error.message : "Could not load your resumes.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingResumes(false);
    }
  };

  // Generate signed URL for document viewing - same logic as ResumeLibraryEnhanced
  const generateSignedUrl = async (filePath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('resumes')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error generating signed URL:', error);
        toast({
          title: "Error",
          description: "Failed to generate file access URL",
          variant: "destructive",
        });
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Error generating signed URL:', error);
      return null;
    }
  };

  const generatePDFBlobUrl = async (filePath: string) => {
    try {
      console.log('DocumentScrapingTest: Starting PDF download for path:', filePath);
      
      const { data, error } = await supabase.storage
        .from('resumes')
        .download(filePath);

      if (error) {
        console.error('DocumentScrapingTest: Error downloading PDF:', error);
        toast({
          title: "Error",
          description: "Failed to download PDF file",
          variant: "destructive",
        });
        return null;
      }

      console.log('DocumentScrapingTest: PDF downloaded successfully, size:', data.size, 'bytes');

      // Create blob URL for PDF.js
      const blob = new Blob([data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      
      console.log('DocumentScrapingTest: Created blob URL:', blobUrl);
      
      return blobUrl;
    } catch (error) {
      console.error('DocumentScrapingTest: Error downloading PDF:', error);
      return null;
    }
  };

  // Handle resume selection and generate proper URL - EXACT SAME LOGIC AS ResumeLibraryEnhanced
  const handleResumeSelection = async (resumeId: string) => {
    setSelectedResumeId(resumeId);
    const resume = userResumes.find(r => r.id === resumeId);
    if (resume) {
      setSelectedResume(resume);
      setScrapedText(''); // Clear previous results
      
      // EXACT SAME LOGIC AS handlePreviewClick in ResumeLibraryEnhanced
      if (!resume.original_file_path) {
        toast({
          title: "No File Available",
          description: "This resume doesn't have an associated file for preview.",
          variant: "destructive",
        });
        return;
      }

      // For PDFs, use blob download approach to avoid CORS issues
      if (resume.file_type === 'application/pdf') {
        const blobUrl = await generatePDFBlobUrl(resume.original_file_path);
        if (blobUrl) {
          setDocumentUrl(blobUrl);
        }
      } else {
        // For Word docs, use signed URL approach
        const url = await generateSignedUrl(resume.original_file_path);
        if (url) {
          setDocumentUrl(url);
        }
      }
    }
  };

  // ACTUAL TEXT SCRAPING FUNCTION - NOT JUST METADATA
  const scrapeDocumentText = async () => {
    if (!selectedResume || !hiddenViewerRef.current) {
      toast({
        title: "Error",
        description: "No document selected or viewer not ready.",
        variant: "destructive"
      });
      return;
    }

    setIsScraping(true);
    setScrapingProgress(0);

    try {
      toast({
        title: "Starting scrape",
        description: "Extracting actual text from rendered document..."
      });

      // Step 1: Wait for viewer to fully render
      setScrapingProgress(20);
      await new Promise(resolve => setTimeout(resolve, 3000)); // Give more time for rendering

      setScrapingProgress(40);
      const viewerContainer = hiddenViewerRef.current;
      let extractedText = '';

      // Step 2: ACTUAL TEXT EXTRACTION based on file type
      if (selectedResume.file_type === 'application/pdf') {
        // PDF TEXT EXTRACTION from PDF.js text layers
        const textLayers = viewerContainer.querySelectorAll('.textLayer');
        console.log('Found PDF text layers:', textLayers.length);
        
        if (textLayers.length > 0) {
          textLayers.forEach((layer, pageIndex) => {
            const pageText = layer.textContent || '';
            if (pageText.trim()) {
              extractedText += `\n=== PAGE ${pageIndex + 1} ===\n${pageText.trim()}\n`;
            }
          });
        }

        // Fallback: Try to get text from span elements in text layer
        if (!extractedText) {
          const textSpans = viewerContainer.querySelectorAll('.textLayer span');
          console.log('Found PDF text spans:', textSpans.length);
          textSpans.forEach(span => {
            extractedText += span.textContent + ' ';
          });
        }

        // Last resort: Try any text content
        if (!extractedText) {
          const allTextNodes = viewerContainer.querySelectorAll('[data-main-rotation]');
          allTextNodes.forEach(node => {
            extractedText += node.textContent + ' ';
          });
        }

      } else {
        // WORD DOCUMENT TEXT EXTRACTION from iframe
        const iframes = viewerContainer.querySelectorAll('iframe');
        console.log('Found Word iframes:', iframes.length);
        
        if (iframes.length > 0) {
          try {
            // Try to access iframe content
            const iframe = iframes[0] as HTMLIFrameElement;
            const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
            
            if (iframeDoc) {
              extractedText = iframeDoc.body.innerText || iframeDoc.body.textContent || '';
            } else {
              extractedText = 'Word document loaded in iframe but content not accessible due to CORS restrictions.';
            }
          } catch (error) {
            console.log('Cannot access iframe content:', error);
            extractedText = 'Word document content not accessible - iframe cross-origin restrictions.';
          }
        }

        // Fallback: Look for any rendered text content
        if (!extractedText) {
          const wordContent = viewerContainer.querySelector('[data-word-content]');
          if (wordContent) {
            extractedText = wordContent.textContent || '';
          }
        }
      }

      setScrapingProgress(80);

      // Process the extracted text
      if (!extractedText || extractedText.trim().length < 10) {
        extractedText = `[NO TEXT EXTRACTED]\nDocument is rendered but text extraction failed.\nFile type: ${selectedResume.file_type}\nViewer elements found: ${viewerContainer.children.length}\nThis might be due to:\n- PDF text layer not loaded yet\n- Word document in protected iframe\n- Document is image-based (scanned)`;
      }

      const finalResults = `=== ACTUAL DOCUMENT TEXT EXTRACTION ===
File: ${selectedResume.file_name || selectedResume.title}
Type: ${selectedResume.file_type}
Extraction Method: ${selectedResume.file_type === 'application/pdf' ? 'PDF.js Text Layer' : 'Word Iframe Content'}
Characters Extracted: ${extractedText.length}
Timestamp: ${new Date().toLocaleString()}

=== EXTRACTED TEXT CONTENT ===
${extractedText.trim()}

=== END EXTRACTION ===`;

      setScrapedText(finalResults);
      setScrapingProgress(100);

      toast({
        title: "Text Extraction Complete!",
        description: `Extracted ${extractedText.length} characters from the rendered document.`
      });

    } catch (error) {
      console.error('Text extraction error:', error);
      toast({
        title: "Extraction failed",
        description: error instanceof Error ? error.message : "Failed to extract text from document.",
        variant: "destructive"
      });
    } finally {
      setIsScraping(false);
    }
  };

  // Image OCR Functions
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      setOcrText(''); // Clear previous results
    } else {
      toast({
        title: "Invalid file",
        description: "Please select an image file",
        variant: "destructive"
      });
    }
  };

  const preprocessImage = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): HTMLCanvasElement => {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Convert to grayscale and increase contrast
    for (let i = 0; i < data.length; i += 4) {
      // Grayscale conversion
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      
      // Increase contrast (simple threshold)
      const threshold = 128;
      const enhanced = gray > threshold ? 255 : 0;
      
      data[i] = enhanced;     // Red
      data[i + 1] = enhanced; // Green  
      data[i + 2] = enhanced; // Blue
      // Alpha stays the same
    }
    
    ctx.putImageData(imageData, 0, 0);
    return canvas;
  };

  const performImageOCR = async () => {
    if (!selectedImage) {
      toast({
        title: "No image selected",
        description: "Please select an image first",
        variant: "destructive"
      });
      return;
    }

    setIsOCRProcessing(true);
    setOcrProgress(0);

    try {
      toast({
        title: "Starting OCR",
        description: "Preprocessing image for better accuracy..."
      });

      // Create canvas and preprocess image
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      // Load image using a simpler approach
      const img = document.createElement('img');
      const imageLoadPromise = new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(selectedImage);
      });

      await imageLoadPromise;

      // Scale up for better OCR (minimum 300 DPI equivalent)
      const scale = Math.max(1, 300 / 72); // Assume 72 DPI input, scale to 300 DPI
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Preprocess for better OCR
      const processedCanvas = preprocessImage(canvas, ctx);
      
      setOcrProgress(10);

      const worker = await createWorker('eng', 1, {
        logger: m => {
          console.log(m);
          if (m.status === 'recognizing text') {
            setOcrProgress(10 + Math.round(m.progress * 80));
          }
        }
      });

      // Better OCR parameters for document text
      await worker.setParameters({
        tessedit_page_seg_mode: '1', // Automatic page segmentation with OSD
        tessedit_ocr_engine_mode: '1', // Neural net LSTM engine only
        tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.,!?@#$%^&*()[]{}":;-_+=|\\/<> \n\t'
      });

      setOcrProgress(20);
      
      const { data: { text, confidence } } = await worker.recognize(processedCanvas);
      
      await worker.terminate();
      
      // Clean up object URL
      URL.revokeObjectURL(img.src);

      const finalResults = `=== IMAGE OCR RESULTS ===
File: ${selectedImage.name}
Size: ${selectedImage.size} bytes
Type: ${selectedImage.type}
Timestamp: ${new Date().toLocaleString()}
Characters Extracted: ${text.length}

=== EXTRACTED TEXT ===
${text.trim()}

=== END OCR ===`;

      setOcrText(finalResults);
      setOcrProgress(100);

      toast({
        title: "OCR Complete!",
        description: `Extracted ${text.trim().length} characters from image`
      });

    } catch (error) {
      console.error('OCR error:', error);
      toast({
        title: "OCR failed",
        description: error instanceof Error ? error.message : "Failed to process image",
        variant: "destructive"
      });
    } finally {
      setIsOCRProcessing(false);
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
      <div className="container mx-auto py-8 space-y-6">
        <PageHeader
          title="Document Scraping Test"
          description="Test scraping text content from rendered PDF and Word documents"
          onBack={() => window.history.back()}
        />

        {/* Debug Info */}
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-yellow-800 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Test Status
            </CardTitle>
          </CardHeader>
          <CardContent className="text-yellow-700">
            <p>User ID: {user?.id || 'Not logged in'}</p>
            <p>Resumes loaded: {userResumes.length}</p>
            <p>Selected: {selectedResume?.title || 'None'}</p>
            <p>Viewer visible: {isViewerVisible ? 'Yes' : 'No'}</p>
          </CardContent>
        </Card>

        {/* Load Resumes */}
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Load Your Resumes</CardTitle>
            <CardDescription>Load resumes from your library to test scraping</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={loadResumes} 
              disabled={isLoadingResumes || !user}
              className="w-full"
            >
              {isLoadingResumes ? "Loading..." : "Load My Resumes"}
            </Button>
            
            {userResumes.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Resume to Test:</label>
                <Select value={selectedResumeId} onValueChange={handleResumeSelection}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a resume to scrape" />
                  </SelectTrigger>
                  <SelectContent>
                    {userResumes.map(resume => (
                      <SelectItem key={resume.id} value={resume.id}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{resume.file_type?.split('/')[1] || 'unknown'}</Badge>
                          {resume.title}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Image OCR Test */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5" />
              Image OCR Test
            </CardTitle>
            <CardDescription>
              Upload an image to extract text using Tesseract.js OCR
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
            
            <div className="flex gap-2">
              <Button 
                onClick={triggerImageUpload}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Select Image
              </Button>
              
              <Button
                onClick={performImageOCR}
                disabled={!selectedImage || isOCRProcessing}
                className="flex-1"
              >
                {isOCRProcessing ? "Processing OCR..." : "Extract Text from Image"}
              </Button>
            </div>

            {selectedImage && imagePreview && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Selected Image:</p>
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="max-h-40 object-contain border rounded"
                  />
                  <Badge className="absolute top-2 right-2 bg-background/80">
                    {selectedImage.name}
                  </Badge>
                </div>
              </div>
            )}

            {isOCRProcessing && (
              <div className="space-y-2">
                <Progress value={ocrProgress} className="w-full" />
                <p className="text-sm text-muted-foreground">
                  OCR Progress: {ocrProgress}%
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Viewer Controls */}
        {selectedResume && (
          <Card>
            <CardHeader>
              <CardTitle>Step 2: Document Viewer</CardTitle>
              <CardDescription>Control viewer visibility and start scraping</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setIsViewerVisible(!isViewerVisible)}
                >
                  {isViewerVisible ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                  {isViewerVisible ? 'Hide Viewer' : 'Show Viewer'}
                </Button>
                
                <Button
                  onClick={scrapeDocumentText}
                  disabled={isScraping}
                  className="flex-1"
                >
                  {isScraping ? "Scraping..." : "Scrape Document Text"}
                </Button>
              </div>

              {isScraping && (
                <div className="space-y-2">
                  <Progress value={scrapingProgress} className="w-full" />
                  <p className="text-sm text-muted-foreground">
                    Scraping progress: {scrapingProgress}%
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Document Viewer */}
        {selectedResume && (
          <Card>
            <CardHeader>
              <CardTitle>Document Viewer (Always Visible for Testing)</CardTitle>
              <CardDescription>
                File: {selectedResume.file_name} ({selectedResume.file_type})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div 
                ref={hiddenViewerRef}
                id="scraping-test-viewer"
                className="w-full h-[600px] border rounded-lg overflow-hidden bg-white"
              >
                {/* EXACT SAME RENDERING LOGIC AS ResumeLibraryEnhanced */}
                {selectedResume && documentUrl && (
                  <>
                    {/* PDF Preview */}
                    {selectedResume.file_type === 'application/pdf' && (
                      <div className="h-[70vh]">
                        <div className="mb-2 text-sm text-muted-foreground">
                          Loading PDF: {selectedResume.title}
                        </div>
                        <PDFViewer 
                          fileUrl={documentUrl}
                          fileName={selectedResume.file_name || selectedResume.title}
                          className="w-full h-full"
                        />
                      </div>
                    )}
                    
                    {/* Word Document Preview - EXACT SAME CONDITIONS */}
                    {(selectedResume.file_type?.includes('wordprocessingml') || 
                      selectedResume.file_type?.includes('msword') ||
                      selectedResume.file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                      selectedResume.file_type === 'application/msword') && (
                      <div className="h-[70vh]">
                        <WordOnlineViewer 
                          documentUrl={documentUrl}
                          fileName={selectedResume.file_name || selectedResume.title}
                          className="w-full h-full"
                          height="100%"
                        />
                      </div>
                    )}
                    
                    {/* Debug info for file type - EXACT SAME AS ResumeLibraryEnhanced */}
                    <div className="mt-4 p-3 bg-muted rounded text-xs">
                      <p><strong>File Type:</strong> {selectedResume.file_type}</p>
                      <p><strong>URL Type:</strong> {documentUrl.startsWith('blob:') ? 'Blob URL (PDF)' : 'Signed URL (Word)'}</p>
                      <p><strong>URL Preview:</strong> {documentUrl.substring(0, 100)}...</p>
                      <p><strong>Original Path:</strong> {selectedResume.original_file_path}</p>
                    </div>
                  </>
                )}
                
                {selectedResume && !documentUrl && (
                  <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                    <div className="text-center">
                      <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No document preview available</p>
                      <p className="text-sm">Unable to access file or file path missing</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Scraping Results */}
        {scrapedText && (
          <Card>
            <CardHeader>
              <CardTitle>Step 3: Scraping Results</CardTitle>
              <CardDescription>
                Text content extracted from the rendered document
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Badge variant="outline">
                    {scrapedText.length} characters extracted
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(scrapedText)}
                  >
                    Copy Results
                  </Button>
                </div>
                
                <pre className="whitespace-pre-wrap text-sm bg-muted p-4 rounded-lg overflow-auto max-h-96 font-mono">
                  {scrapedText}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Image OCR Results */}
        {ocrText && (
          <Card>
            <CardHeader>
              <CardTitle>Image OCR Results</CardTitle>
              <CardDescription>
                Text content extracted from the uploaded image
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Badge variant="outline">
                    {ocrText.length} characters extracted
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(ocrText)}
                  >
                    Copy OCR Results
                  </Button>
                </div>
                
                <pre className="whitespace-pre-wrap text-sm bg-muted p-4 rounded-lg overflow-auto max-h-96 font-mono">
                  {ocrText}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}